import React, { useState, useEffect } from 'react';
import { Star, Users, Calendar, MapPin, Heart, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import AffiliateSection from '../components/AffiliateSection';
import NewsletterSignup from '../components/NewsletterSignup';
import Footer from '../components/Footer';
import TeamSelectionModal from '../components/TeamSelectionModal';
import SportsAssistant from '../components/SportsAssistant';
import { useTheme } from '../contexts/ThemeContext';
import testimonials from '../config/testimonials';

const Home = () => {
  const [showTeamModal, setShowTeamModal] = useState(false);
  const { selectedTeam, teamColors, selectTeam, isTeamSelected } = useTheme();

  // Show team modal on first visit
  useEffect(() => {
    const hasVisited = localStorage.getItem('hasVisited');
    if (!hasVisited && !isTeamSelected) {
      setShowTeamModal(true);
      localStorage.setItem('hasVisited', 'true');
    }
  }, [isTeamSelected]);

  const handleTeamSelect = (team) => {
    selectTeam(team);
    setShowTeamModal(false);
  };

  // testimonials now sourced from config/testimonials.js

  const howItWorksSteps = [
    {
      icon: Users,
      title: "Find Your Crew",
      description: "Connect with fellow fans planning trips to the same games"
    },
    {
      icon: Calendar,
      title: "Plan Together",
      description: "Coordinate travel dates, accommodations, and game day activities"
    },
    {
      icon: MapPin,
      title: "Travel Smart",
      description: "Book group rates on flights, hotels, and experiences"
    },
    {
      icon: Heart,
      title: "Create Memories",
      description: "Experience unforgettable game days with your new crew"
    }
  ];

  return (
    <div 
      className="home-root min-h-screen"
      style={{
        background: selectedTeam 
          ? `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 50%, ${teamColors.primary} 100%)`
          : 'linear-gradient(135deg, #0ea5e9 0%, #f97316 50%, #0ea5e9 100%)'
      }}
    >
      <Header />
      
      <TeamSelectionModal 
        isOpen={showTeamModal}
        onSelectTeam={handleTeamSelect}
        onClose={() => setShowTeamModal(false)}
      />

      {/* Hero Section with Football Field Theme */}
      <section 
        className="relative py-20 overflow-hidden football-field-bg"
        style={{
          background: selectedTeam 
            ? `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 50%, ${teamColors.primary} 100%)`
            : 'linear-gradient(135deg, #0ea5e9 0%, #f97316 50%, #0ea5e9 100%)'
        }}
      >
        {/* Football field decorations */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Yard line markers */}
          <div 
            className="absolute top-0 left-1/4 w-2 h-full opacity-20"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          <div 
            className="absolute top-0 left-2/4 w-3 h-full opacity-30"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          <div 
            className="absolute top-0 left-3/4 w-2 h-full opacity-20"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          
          {/* Goal posts */}
          <div 
            className="absolute top-10 left-10 w-12 h-12 goalpost opacity-30"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          <div 
            className="absolute top-10 right-10 w-12 h-12 goalpost opacity-30"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          
          {/* Football shapes */}
          <div 
            className="absolute bottom-20 left-20 w-16 h-12 football-shape football-laces opacity-15 animate-float"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          <div 
            className="absolute top-32 right-32 w-20 h-14 football-shape football-laces opacity-10 animate-float"
            style={{ 
              backgroundColor: teamColors.accent,
              animationDelay: '2s'
            }}
          ></div>
        </div>
        
        <div className="absolute inset-0 bg-black opacity-30"></div>
        <div className="relative container mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 drop-shadow-lg">
            {selectedTeam ? `Go ${selectedTeam.name.split(' ').pop()}!` : 'Your GameDay Starts Here'}
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto drop-shadow-md">
            {selectedTeam 
              ? `Plan the perfect trip for ${selectedTeam.name} games with fellow fans`
              : 'Connect with fans, plan epic trips, and experience sports like never before'
            }
          </p>
          <button 
            onClick={() => setShowTeamModal(true)}
            className="inline-flex items-center space-x-2 bg-white text-gray-900 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all duration-200 transform hover:scale-105 shadow-2xl"
          >
            <Users size={24} />
            <span>{selectedTeam ? 'Change Team' : 'Choose Your Team'}</span>
          </button>
        </div>
      </section>

      {/* Sports Assistant - Bold Team Colors */}
      <section 
        className="py-16 relative overflow-hidden pattern-football-lines"
        style={{
          background: selectedTeam 
            ? `linear-gradient(45deg, ${teamColors.primary} 0%, ${teamColors.secondary} 50%, ${teamColors.primary} 100%)`
            : 'linear-gradient(45deg, #0ea5e9 0%, #f97316 50%, #0ea5e9 100%)'
        }}
      >
        <div className="absolute inset-0 bg-black opacity-20 pointer-events-none"></div>
        {/* Football field elements */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Sideline decorations */}
          <div 
            className="absolute top-0 left-0 w-full h-4 sideline-pattern opacity-30"
            style={{ borderTop: `4px solid ${teamColors.accent}` }}
          ></div>
          <div 
            className="absolute bottom-0 left-0 w-full h-4 sideline-pattern opacity-30"
            style={{ borderBottom: `4px solid ${teamColors.accent}` }}
          ></div>
          
          {/* Endzone markers */}
          <div 
            className="absolute top-10 left-10 w-8 h-24 endzone-pattern opacity-40"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          <div 
            className="absolute bottom-10 right-10 w-8 h-24 endzone-pattern opacity-40"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
        </div>
        
        <div className="container mx-auto px-6 relative">
          <div className="text-center mb-12">
            <div 
              className="inline-block w-16 h-1 rounded-full mb-4"
              style={{ backgroundColor: teamColors.accent }}
            ></div>
            <h2 className="text-4xl font-bold text-white mb-4 drop-shadow-lg">
              Plan Your Perfect GameDay Trip
            </h2>
            <p className="text-xl text-white opacity-90 max-w-2xl mx-auto drop-shadow-md">
              Tell us what game you're attending and we'll create a personalized travel plan with the best deals
            </p>
          </div>
          <SportsAssistant teamColors={teamColors} />
        </div>
      </section>

      {/* Affiliate Partners with Bold Team Colors */}
      <div 
        className="border-t-4"
        style={{ borderColor: teamColors.accent }}
      >
        <AffiliateSection />
      </div>

      {/* How It Works with Bold Team Colors - HIDDEN */}
      {false && (
        <section 
        className="py-16 relative overflow-hidden football-field-subtle"
        style={{
          background: selectedTeam 
            ? `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 50%, ${teamColors.primary} 100%)`
            : 'linear-gradient(135deg, #0ea5e9 0%, #f97316 50%, #0ea5e9 100%)'
        }}
      >
        <div className="absolute inset-0 bg-black opacity-20 pointer-events-none"></div>
        {/* Football field yard lines */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Major yard lines */}
          <div 
            className="absolute top-0 left-1/5 w-1 h-full opacity-25"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          <div 
            className="absolute top-0 left-2/5 w-1 h-full opacity-25"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          <div 
            className="absolute top-0 left-3/5 w-1 h-full opacity-25"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          <div 
            className="absolute top-0 left-4/5 w-1 h-full opacity-25"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          
          {/* Hash marks */}
          <div 
            className="absolute top-20 left-0 w-full h-1 opacity-25"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          <div 
            className="absolute bottom-20 left-0 w-full h-1 opacity-25"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          
          {/* Football decorative elements */}
          <div 
            className="absolute top-16 left-16 w-12 h-8 football-shape football-laces opacity-30 animate-float"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          <div 
            className="absolute bottom-16 right-16 w-10 h-7 football-shape football-laces opacity-30 animate-float"
            style={{ 
              backgroundColor: teamColors.accent,
              animationDelay: '1.5s'
            }}
          ></div>
        </div>
        
        <div className="container mx-auto px-6 relative">
          <div className="text-center mb-16">
            <div className="flex justify-center items-center mb-4">
              <div 
                className="w-8 h-1 rounded-full mr-2"
                style={{ backgroundColor: teamColors.accent }}
              ></div>
              <div 
                className="w-4 h-1 rounded-full mr-2"
                style={{ backgroundColor: teamColors.accent }}
              ></div>
              <div 
                className="w-2 h-1 rounded-full"
                style={{ backgroundColor: teamColors.accent }}
              ></div>
            </div>
            <h2 className="text-4xl font-bold text-white mb-4 drop-shadow-lg">How 40 Bags Works</h2>
            <p className="text-xl text-white opacity-90 drop-shadow-md">Join thousands of fans who travel smarter</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorksSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div 
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 shadow-lg animate-float"
                  style={{ 
                    backgroundColor: teamColors.accent,
                    animationDelay: `${index * 0.5}s`
                  }}
                >
                  <step.icon size={32} style={{ color: teamColors.primary }} />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3 drop-shadow-md">{step.title}</h3>
                <p className="text-white opacity-80 drop-shadow-sm">{step.description}</p>
                {/* Step indicator */}
                <div className="mt-4 flex justify-center">
                  <div 
                    className="w-8 h-1 rounded-full"
                    style={{ backgroundColor: teamColors.accent }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Testimonials with Bold Team Colors */}
      <section 
        className="py-16 relative overflow-hidden pattern-grid"
        style={{
          background: selectedTeam 
            ? `linear-gradient(to right, ${teamColors.primary} 0%, ${teamColors.secondary} 50%, ${teamColors.primary} 100%)`
            : 'linear-gradient(to right, #0ea5e9 0%, #f97316 50%, #0ea5e9 100%)'
        }}
      >
        <div className="absolute inset-0 bg-black opacity-20 pointer-events-none"></div>
        {/* Football field background elements */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Corner goal posts */}
          <div 
            className="absolute top-10 left-10 w-8 h-8 goalpost opacity-40"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          <div 
            className="absolute top-10 right-10 w-8 h-8 goalpost opacity-40"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          <div 
            className="absolute bottom-10 left-10 w-8 h-8 goalpost opacity-40"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          <div 
            className="absolute bottom-10 right-10 w-8 h-8 goalpost opacity-40"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          
          {/* Football field decorations */}
          <div 
            className="absolute top-1/3 left-1/4 w-6 h-4 football-shape opacity-30"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          <div 
            className="absolute bottom-1/3 right-1/4 w-8 h-5 football-shape opacity-30"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
        </div>
        
        <div className="container mx-auto px-6 relative">
          <div className="text-center mb-16">
            <div 
              className="inline-block w-20 h-1 rounded-full mb-4"
              style={{ backgroundColor: teamColors.accent }}
            ></div>
            <h2 className="text-4xl font-bold text-white mb-4 drop-shadow-lg">What Fans Are Saying</h2>
            <p className="text-xl text-white opacity-90 drop-shadow-md">Join the community that's changing how fans travel</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="card-field p-6 relative overflow-hidden group hover:shadow-2xl transition-all duration-300"
                style={{
                  borderTop: `4px solid ${teamColors.accent}`,
                  borderLeft: `2px solid ${teamColors.accent}30`
                }}
              >
                {/* Football accent instead of circle */}
                <div 
                  className="absolute top-2 right-2 w-8 h-6 football-shape opacity-15 group-hover:opacity-25 transition-opacity"
                  style={{ backgroundColor: teamColors.accent }}
                ></div>
                
                <div className="flex items-center mb-4 relative z-10">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} className="text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 relative z-10">"{testimonial.text}"</p>
                <p className="font-semibold text-gray-900 relative z-10">- {testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section with Bold Team Colors */}
      <section 
        className="py-16 relative overflow-hidden football-field-subtle"
        style={{
          background: selectedTeam 
            ? `linear-gradient(to right, ${teamColors.primary} 0%, ${teamColors.secondary} 50%, ${teamColors.primary} 100%)`
            : 'linear-gradient(to right, #0ea5e9 0%, #f97316 50%, #0ea5e9 100%)'
        }}
      >
        <div className="absolute inset-0 bg-black opacity-20 pointer-events-none"></div>
        {/* Football field yard markers */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Sideline borders */}
          <div 
            className="absolute top-0 left-0 w-full h-2 opacity-40"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          <div 
            className="absolute bottom-0 left-0 w-full h-2 opacity-40"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          
          {/* Yard markers */}
          <div 
            className="absolute top-10 left-1/6 w-2 h-16 opacity-30"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          <div 
            className="absolute bottom-10 right-1/6 w-2 h-16 opacity-30"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          
          {/* Football elements */}
          <div 
            className="absolute top-1/4 left-1/8 w-10 h-7 football-shape football-laces opacity-25 animate-float"
            style={{ backgroundColor: teamColors.accent }}
          ></div>
          <div 
            className="absolute bottom-1/4 right-1/8 w-12 h-8 football-shape football-laces opacity-25 animate-float"
            style={{ 
              backgroundColor: teamColors.accent,
              animationDelay: '3s'
            }}
          ></div>
        </div>
        
        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center items-center mb-6">
              <div 
                className="w-12 h-1 rounded-full mr-3"
                style={{ backgroundColor: teamColors.accent }}
              ></div>
              <div 
                className="w-8 h-1 rounded-full mr-3"
                style={{ backgroundColor: teamColors.accent }}
              ></div>
              <div 
                className="w-4 h-1 rounded-full"
                style={{ backgroundColor: teamColors.accent }}
              ></div>
            </div>
            <h2 className="text-4xl font-bold text-white mb-8 drop-shadow-lg">Gathering Through Time: 20+ Years</h2>
            
            
            <p className="text-lg text-white opacity-90 mb-8 drop-shadow-md">
              For over two decades, 40 Bags has been the trusted companion for sports fans seeking 
              authentic travel experiences. We've evolved from a simple travel coordination service 
              to a comprehensive platform that connects passionate fans with unforgettable game day adventures.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              {[
                "Verified Travel Partners",
                "Best Price Guarantee", 
                "24/7 Support"
              ].map((feature, index) => (
                <div key={index} className="flex items-center space-x-2 text-white">
                  <CheckCircle size={20} />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter with Team Border */}
      <div 
        className="border-t-4"
        style={{ borderColor: teamColors.accent }}
      >
        <NewsletterSignup />
      </div>
      
      <Footer />
    </div>
  );
};

export default Home; 